import './style.scss';

import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import type { ChangeEvent, FC } from 'react';
import { useState } from 'react';
import DataTable from 'react-data-table-component';
import { toast as Toast } from 'react-toastify';
import { Alert, Button, Collapse, Modal, Spinner, Tooltip } from 'reactstrap';

import { enrichCigarHypothesis } from '@/api/cigar-matching';
import Dropzone from '@/components/dropzone';
import { Constants } from '@/constants';

const CIGAR_IMPORT_FIELDS = [
  'brand',
  'name',
  'wrapper',
  'binder',
  'filler',
  'strength',
] as const;

type CigarImportField = (typeof CIGAR_IMPORT_FIELDS)[number];
type CigarImportRecord = Record<CigarImportField, string>;
type CigarImportType = 'single_import' | 'bulk_import';
type CsvRecord = Record<string, unknown>;
type ImportedVitola = Record<string, unknown>;

interface ImportedCigar {
  brand?: string;
  name?: string;
  wrapper?: string | string[];
  binder?: string | string[];
  filler?: string | string[];
  strength?: string | number;
  vitolas?: ImportedVitola[];

  [field: string]: unknown;
}

interface CigarImporterProps {
  open: boolean;
  onClose: () => void;
  importVitolasOnly?: boolean;
  onRecievedCigar?: (cigar: ImportedCigar) => void;
  onReceivedVitolas?: (vitolas: ImportedVitola[]) => void;
}

interface CigarDatasourceResponse {
  id?: string;
  signed_url: string;
  datasource_url?: string;
}

const hasText = (value: unknown) =>
  typeof value === 'string' && value.trim().length > 0;

const isUsableScrapeResponse = (cigar: ImportedCigar, vitolasOnly: boolean) => {
  if (vitolasOnly) return Boolean(cigar.vitolas?.length);
  return hasText(cigar.brand) && hasText(cigar.name);
};

const sendToDevs = (url: string) => {
  // TODO Email devs with the URL that does not scrape properly - for now, just log error to Sentry
  Sentry.captureException(`Unable to parse url: ${url}`);
};

const CigarImporter: FC<CigarImporterProps> = ({
  open,
  onClose,
  importVitolasOnly = false,
  onRecievedCigar = () => {},
  onReceivedVitolas = () => {},
}) => {
  const [selectedRecords, setSelectedRecords] = useState<CigarImportRecord[]>(
    []
  );
  const [selectedRecordCount, setSelectedRecordCount] = useState(0);
  const [showImporterDatatable, setShowImporterDatatable] = useState(false);
  const [cigarImportType, setCigarImportType] =
    useState<CigarImportType>('single_import');
  const [selectedUrl, setSelectedUrl] = useState('');
  const [showUrlTooltip, setShowUrlTooltip] = useState(false);
  const [selectedCigarsFile, setSelectedCigarsFile] = useState<File | null>(
    null
  );
  const [csvError, setCsvError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [unableToParse, setUnableToParse] = useState(false);

  const onSave = async () => {
    console.log(`Getting cigar details for ${selectedUrl}`);
    setImporting(true);
    setUnableToParse(false);
    try {
      let importedCigar: ImportedCigar;
      try {
        const response = await axios.get<ImportedCigar>(
          `${Constants.apiPath}/scrape/cigar`,
          {
            params: { url: selectedUrl },
          }
        );
        importedCigar = response.data;
        if (!isUsableScrapeResponse(importedCigar, importVitolasOnly)) {
          throw new Error('The scraper returned no usable cigar details.');
        }
      } catch (scrapeError) {
        console.warn(
          'Legacy cigar scrape failed; falling back to AI enrichment.',
          scrapeError
        );
        const enrichment = await enrichCigarHypothesis({
          sourceUrl: selectedUrl,
          cigar: {
            brand: null,
            name: null,
            line: null,
            aliases: [],
            countryOfOrigin: null,
            wrapper: null,
            binder: null,
            filler: null,
            strength: null,
          },
        });
        const enriched = enrichment.cigar || enrichment;
        importedCigar = {
          ...enriched,
          origin: enriched.origin || enriched.countryOfOrigin,
          review_summary:
            enriched.review_summary || enriched.reviewSummary || null,
          faqs: Array.isArray(enriched.faqs)
            ? JSON.stringify(enriched.faqs)
            : enriched.faqs,
        };
      }

      console.debug(JSON.stringify(importedCigar));
      if (importedCigar.vitolas && importVitolasOnly) {
        onReceivedVitolas(importedCigar.vitolas);
      }
      if (!importVitolasOnly) {
        onRecievedCigar(importedCigar);
      }
      if (typeof onClose === 'function') onClose();
    } catch (error) {
      setUnableToParse(true);
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const onImport = () => {
    if (!selectedCigarsFile) {
      setCsvError('Select a CSV file before importing.');
      setShowImporterDatatable(false);
      return;
    }

    console.log('Uploading csv file...');
    setUploading(true);
    // FIXME This needs to be an endpoint the business panel can access, as well... have an endpoint on the business
    //  panel just use a JWT to access the admin endpoint
    axios
      .post<CigarDatasourceResponse>(
        `${Constants.apiPath}/cigars/datasources/sign`,
        {
          filetype: selectedCigarsFile.type,
        }
      )
      .then((result) => {
        console.log('Signed response:');
        console.log(JSON.stringify(result.data.signed_url));
        const uploadOptions = {
          method: 'PUT',
          body: selectedCigarsFile,
        };

        console.log(result.data);

        console.log('Direct upload in progress...');
        fetch(result.data.signed_url, uploadOptions)
          .then(async () => {
            console.debug('S3 success');
            if (result.data.datasource_url) {
              // FIXME This needs to be an endpoint the business panel can access, as well...
              const batchEndpoint = `${Constants.apiPath}/cigars/batch`;
              console.debug('Kicking off step function...');
              await axios
                .post<void>(batchEndpoint, {
                  datasource_url: result.data.datasource_url,
                })
                .then(() => {
                  // TODO Update local state with the new datasource? Or just show alert that it's importing and they will be notified when complete?
                  setUploading(false);
                  setShowImporterDatatable(false);
                  if (typeof onClose === 'function') {
                    onClose();
                  }
                  Toast.success('You will receive an email when complete', {
                    heading: 'Cigar Sync Started',
                    position: 'top-right',
                  });
                })
                .catch((error: unknown) => {
                  console.error(error);
                  setUploading(false);
                  // TODO Alert the user with ModalDialog? Reuse it for all of these catches...
                  alert(
                    'Something went wrong. Please contact our support team at support@boxpressd.com'
                  );
                });
            } else {
              console.error('No data URL...');
              setUploading(false);
              // Alert admins - something went wrong
              Sentry.setExtra('s3_signed_url_response', {
                id: result.data.id,
                datasource_url: result.data.datasource_url,
              });
              Sentry.captureException('Unable to upload media to S3');
              alert(
                'Something went wrong. Please contact our support team at support@boxpressd.com'
              );
            }
          })
          .catch((error: unknown) => {
            setUploading(false);
            console.debug('S3 failure:');
            console.debug(error);
            alert(
              'Something went wrong. Please contact our support team at support@boxpressd.com'
            );
          });
      })
      .catch((error: unknown) => {
        setUploading(false);
        console.error(error);
        alert(
          'Something went wrong. Please contact our support team at support@boxpressd.com'
        );
      });
  };

  const renderImportModal = () => (
    <Modal isOpen={open && !showImporterDatatable} toggle={onClose}>
      <div className="modal-header">
        <h5 className="modal-title">
          {importVitolasOnly ? 'Import Vitolas' : 'Import Cigars'}
        </h5>
      </div>
      <div className="modal-body">
        <RadioGroup
          row
          aria-label="product import type"
          name="product_import_type"
          value={cigarImportType}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setCigarImportType(event.target.value as CigarImportType)
          }
        >
          <FormControlLabel
            value="single_import"
            control={<Radio color="primary" />}
            label={
              importVitolasOnly ? 'Vitolas from URL' : 'Single cigar from URL'
            }
            labelPlacement="end"
          />
          {!importVitolasOnly && (
            <FormControlLabel
              value="bulk_import"
              control={<Radio color="primary" />}
              label="Bulk import from CSV"
              labelPlacement="end"
            />
          )}
        </RadioGroup>
        <Collapse isOpen={cigarImportType === 'single_import'}>
          {'URL'} <i className="icon-info-alt" id="urlTooltip" />
          <Tooltip
            placement="top"
            isOpen={showUrlTooltip}
            target="urlTooltip"
            toggle={() => setShowUrlTooltip(!showUrlTooltip)}
            style={{ width: 300 }}
          >
            Importable URLs include: <br />
            <br />
            <ul>
              <li>https://www.cigarsinternational.com/p/[*]</li>
              <li>
                {'https://www.jrcigars.com/item/[*]'}
                <strong style={{ color: 'red' }}>
                  (currently unavailable)
                </strong>
              </li>
              <li>https://www.famous-smoke.com/[*]</li>
              <li>https://www.neptunecigar.com/cigar/[*]</li>
              <li>https://halfwheel.com/[*]</li>
            </ul>
          </Tooltip>
          <input
            className="form-control"
            type="text"
            name="import-url"
            value={selectedUrl}
            onChange={(e) => setSelectedUrl(e.target.value)}
          />
          {unableToParse && (
            <Alert color="danger" style={{ marginTop: 10 }}>
              <span>Unable to parse URL. Please try a different one.</span>
              <Button
                color="danger"
                onClick={() => {
                  sendToDevs(selectedUrl);
                  setUnableToParse(false);
                  setSelectedUrl('');
                  Toast.success(
                    'URL sent to developers, please try a different one.',
                    {
                      position: 'top-right',
                    }
                  );
                }}
              >
                Request Fix
              </Button>
            </Alert>
          )}
        </Collapse>
        <Collapse isOpen={cigarImportType === 'bulk_import'}>
          <div>CSV File Upload</div>
          <Dropzone
            id="dropzone"
            accept={{
              'text/csv': ['.csv'],
              'application/vnd.ms-excel': ['.csv'],
            }}
            dropText="Add CSV file or drop here to upload"
            onChange={(files) => {
              setCsvError(null);
              setSelectedCigarsFile(files[0] || null);
            }}
            getJson={(json: CsvRecord[]) => {
              if (!json.length) {
                setCsvError('The CSV file does not contain any records.');
                return;
              }

              const headers = Object.keys(json[0]).reduce(
                (result, header) => ({
                  ...result,
                  [header.trim().toLowerCase()]: header,
                }),
                {} as Record<string, string>
              );
              const missingFields = CIGAR_IMPORT_FIELDS.filter(
                (field) => !headers[field]
              );

              if (missingFields.length) {
                setCsvError(
                  `Missing required CSV columns: ${missingFields.join(', ')}.`
                );
                return;
              }

              const previewRecords = json.slice(0, 10).map((record) =>
                CIGAR_IMPORT_FIELDS.reduce(
                  (result, field) => ({
                    ...result,
                    [field]: String(record[headers[field]] ?? ''),
                  }),
                  {} as CigarImportRecord
                )
              );

              setSelectedRecords(previewRecords);
              setSelectedRecordCount(json.length);
              setShowImporterDatatable(true);
            }}
            style={{ minHeight: 'auto' }}
          />
          {csvError && (
            <Alert color="danger" style={{ marginTop: 10 }}>
              {csvError}
            </Alert>
          )}
        </Collapse>
      </div>
      <div className="modal-footer">
        <Button color="light" onClick={onClose}>
          {'Cancel'}
        </Button>
        {cigarImportType === 'single_import' && (
          <Button
            onClick={onSave}
            variant="contained"
            color="primary"
            disabled={importing || !selectedUrl}
          >
            {importing && (
              <Spinner color="dark" style={{ marginRight: 8 }} size="sm" />
            )}
            {importing ? 'Importing' : 'Import'}
          </Button>
        )}
      </div>
    </Modal>
  );

  const renderImporterDatatableModal = () => {
    if (selectedRecords && selectedRecordCount) {
      console.log(
        `Showing the first ${selectedRecords.length < 10 ? selectedRecords.length : 10} out of ${selectedRecordCount} records`
      );
    }
    return (
      <Modal
        isOpen={showImporterDatatable}
        toggle={() => setShowImporterDatatable(false)}
        onClosed={() => setShowImporterDatatable(false)}
      >
        <div className="modal-header">
          <h5 className="modal-title h2">Confirm</h5>
        </div>
        <div className="modal-body">
          {selectedRecords && selectedRecordCount && (
            <div>
              <div style={{ marginTop: 26 }}>
                {`Showing the first ${selectedRecords.length < 10 ? selectedRecords.length : 10} out of ${selectedRecordCount} records`}
              </div>
              <DataTable
                noHeader
                pagination={false}
                columns={CIGAR_IMPORT_FIELDS.map((name) => ({
                  name: name
                    .replace('_', ' ')
                    .replace('_', ' ')
                    .split(' ')
                    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                    .join(' '),
                  selector: (row: CigarImportRecord) => row[name],
                  wrap: true,
                }))}
                data={selectedRecords}
                selectableRows={false}
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button onClick={() => setShowImporterDatatable(false)}>
            Cancel
          </Button>
          <Button
            disabled={uploading}
            onClick={onImport}
            variant="contained"
            color="primary"
          >
            {uploading && (
              <Spinner
                type="border"
                color="dark"
                size="sm"
                style={{ marginRight: 10 }}
              />
            )}
            {uploading ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      {renderImportModal()}
      {renderImporterDatatableModal()}
    </>
  );
};

export default CigarImporter;
